import { useMutation, useQueryClient } from '@tanstack/react-query';
import StationsApi from '../lib/api/StationsApi';

/**
 * Create/update mutations backing the Stations table and its detail dialog.
 *
 * The Redux `STATION_UPDATE_SUCCESS` reducer did TWO things: it replaced the
 * matching row in the list (so the grid updates) AND set `selection` to the
 * updated station (so an already-open detail dialog updates too, since
 * `PartnerTypeField`/`StationOperationalField` render off the `station` prop,
 * not a store read). Reproducing only the list half leaves an open dialog
 * showing stale values, so both mutations:
 *   1. invalidate the `['stations']` list (prefix match — catches every
 *      paged/filtered cache entry), and
 *   2. invalidate the single-station cache entry AND return the updated
 *      station, so the caller can push it into whatever local state backs
 *      the open dialog.
 *
 * Both mutate functions resolve with `{ success, station }` rather than
 * throwing, matching the old thunks — every call site does
 * `const { success } = await updateStation(...)` and toasts on failure, and
 * throwing here would break them all.
 */
const useStationMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (input) => StationsApi.createStation({ input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stations'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }) => StationsApi.updateStation(id, patch),
    onSuccess: (station, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['stations'] });
      queryClient.invalidateQueries({ queryKey: ['station', id] });
    },
  });

  const createStation = async (input) => {
    try {
      const station = await createMutation.mutateAsync(input);
      return { success: true, station };
    } catch {
      return { success: false };
    }
  };

  const updateStation = async (id, patch) => {
    try {
      const station = await updateMutation.mutateAsync({ id, patch });
      return { success: true, station };
    } catch {
      return { success: false };
    }
  };

  return {
    createStation,
    updateStation,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
  };
};

export default useStationMutations;
